import React, { useState, useContext } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { api } from '../utils/axiosConfig';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import CryptoJS from 'crypto-js';
import { authManager } from '../utils/auth';
import { UserContext } from '../contexts/UserContext';

const Base_Url = process.env.REACT_APP_API_BASE_URL;
// SECURITY WARNING: AES key should be moved to backend for production
// Frontend encryption keys are visible to users and not secure
const AES_KEY = process.env.REACT_APP_ENCRYPTION_AES_KEY;


const Login = () => {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [loading, setLoading] = useState(false);
    const navigate = useNavigate();
    const { setUser } = useContext(UserContext);

    const encryptData = (data, key = AES_KEY) => {
        const dataStr = typeof data === 'object' ? JSON.stringify(data) : data;
        return CryptoJS.AES.encrypt(dataStr, key).toString();
    };

    const handleLogin = async (e) => {
        e.preventDefault();
        setLoading(true);

        try {
            const encryptedPayload = encryptData({ email, password });
            // Must use the `api` instance (withCredentials: true) - the backend
            // sets the httpOnly auth cookies on this response, and a plain axios
            // call without credentials would silently drop the Set-Cookie header.
            const response = await api.post(`${Base_Url}/users/login`, {
                payload: encryptedPayload
            });

            if (response.data.status_code === 200) {
                const result = response.data.result;

                // Store only display-safe, non-sensitive data in localStorage.
                // Standardize field access - handle both response shapes
                const userId = result.userId || result.user?.id;
                const userName = result.name || result.user?.name;
                const userEmail = result.email || result.user?.email;
                const userRole = result.role || result.user?.role;

                localStorage.setItem('userId', userId);
                localStorage.setItem('name', userName);
                localStorage.setItem('userEmail', userEmail);
                localStorage.setItem('role', userRole);

                // The backend sets the httpOnly auth cookies on this response either
                // way (see somo-circle1-api Issue #302), but the SPA should only treat
                // the user as logged in - and let ProtectedRoute admit them - once MFA
                // is actually satisfied. When mfaRequired, Authentication_login.js is
                // the one that calls markLoggedIn()/init() after MFA succeeds.
                if (!result?.mfaRequired) {
                    authManager.markLoggedIn();
                    authManager.init();
                }

                setUser({ name: userName, userId: userId });

                toast.success("Login successful!");
                setTimeout(() => {
                    navigate(result?.mfaRequired ? '/authenticate' : '/dashboard');
                }, 3000);

                return;
            } else {
                throw new Error("Unexpected response");
            }
        } catch (error) {
            const errorMsg =
                error.response?.data?.message ||
                error.response?.data?.error?.message ||
                "Login failed. Please try again.";
            toast.error(errorMsg);
            setLoading(false);
        }
    };

    return (
        <div className='hold-transition login-page accent-custom'>
            <ToastContainer autoClose={3000} />
            <div className="login-box login_div">
                <div className="card card-outline card-primary">
                    <div className="card-header text-center">
                        <Link to="/" className="login_logo"><img src="assets/img/logo.png" alt='logo.png' border={0} /></Link>
                    </div>
                    <div className="card-body d-flex flex-row flex_flow_rowwrap">
                        <div className="col-12 col-sm-12 col-md-12">
                            <h5 className="login-box-msg mb-3">Admin Login</h5>
                            <form onSubmit={handleLogin}>
                                <div className="input-group mb-3">
                                    <input
                                        type="email"
                                        className="form-control"
                                        placeholder="Username"
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                    />
                                    <div className="input-group-append">
                                        <div className="input-group-text">
                                            <i className="fa-solid fa-user" />
                                        </div>
                                    </div>
                                </div>
                                <div className="input-group mb-3">
                                    <input
                                        type="password"
                                        className="form-control"
                                        placeholder="Password"
                                        value={password}
                                        onChange={(e) => setPassword(e.target.value)}
                                    />
                                    <div className="input-group-append">
                                        <div className="input-group-text">
                                            <i className="fas fa-lock" />
                                        </div>
                                    </div>
                                </div>
                                <div className="row">
                                    <div className="col-12 txt_rgt">
                                        <div className="pt-1">
                                            <Link to="/forget" className="f_black">Forgot Password</Link>
                                        </div>
                                    </div>
                                </div>
                                <div className="row">
                                    <div className="col-12 mt-3">
                                        <button type="submit" className="btn btn-blue btn-block" disabled={loading}>
                                            {loading ? 'Loading...' : 'Login'}
                                        </button>
                                    </div>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Login;
